/* eslint-disable consistent-return */
import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { Grid } from '@campaignhub/suit-theme'

import { toggleArray } from '@campaignhub/javascript-utils'
import { useSetState } from '@campaignhub/react-hooks'
import PageContext from '@contexts/pageContext'

import { getEntityByName } from '@functions/getEntityByName'
import { getServiceStatusColor } from '@functions/getServiceStatusColor'
import { isCurrentVersionDelivered } from '@functions/isCurrentVersionDelivered'

import GridItem from './GridItem'

const defaultState = {
  currentlySelectedIds: [],
}

const GridView = (props) => {
  const {
    callbacks: { toggleUpdateSelectedIds },
    consolidateContent, contents, disableAssetLink, maxSelection, multipleSelection, selectedIds, sortDirection, sortKey, showDetails,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { currentlySelectedIds } = state

  const { serviceJob } = useContext(PageContext)

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceGroups, serviceJobStatuses } = entities

  const sentOrChangeRequestIds = new Set([
    getEntityByName(serviceJobStatuses, 'SentToClient')?.id,
    getEntityByName(serviceJobStatuses, 'ChangeRequest')?.id,
  ].filter(Boolean))

  const selectContent = (content) => {
    if (maxSelection) {
      if (maxSelection <= selectedIds.length && !selectedIds.includes(content.id)) {
        return (
          swal.fire({
            title: 'Maximum Number of<br/>Assets Selected',
            html: `This job requires only one (${maxSelection}) proof.<br/> 
            Please deliver ${maxSelection} asset${maxSelection > 1 ? 's' : ''} to the client.`,
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e2001a',
          })
        )
      }
    }

    setState({
      currentlySelectedIds: toggleArray((selectedIds.includes(content.id)) ? selectedIds
        : selectedIds.filter(id => id !== content.id), content.id),
    })
  }

  useEffect(() => {
    toggleUpdateSelectedIds(currentlySelectedIds)
  }, [currentlySelectedIds])

  const file = contents.find(x => x.file)
  const fileUrl = file?.file.downloadUrl || file?.assetUrl

  // URL-only assets (e.g. Virtual_Tour) have no file, so read the sort field off the asset itself.
  // With no originalName to compare they all tie, which keeps them in upload order.
  const sortValue = content => `${content.file?.[sortKey] || content[sortKey] || ''}`.toUpperCase()

  return (
    <Grid
      gridAutoRows="1fr"
      gridGap="large"
      gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
      maxHeight="572px"
      overflowY="auto"
    >
      {consolidateContent && contents.length > 0 && (
        <GridItem
          badgeColor={getServiceStatusColor(serviceJobStatuses[contents[0].serviceJobStatusId]?.name)}
          badgeText={serviceJobStatuses[contents[0].serviceJobStatusId]?.description}
          content={contents[0]}
          fileUrl={fileUrl ? `${fileUrl}` : null}
          iconName={serviceGroups[serviceJob.serviceGroupId]?.name}
          link={`/service-jobs/${serviceJob.id}/assets?assetId=${contents[0].id}`}
          name={serviceGroups[serviceJob.serviceGroupId]?.name === 'Copywriting' ? 'Build Copy' : 'Consolidated File'}
          notSentYet={false}
          showDetails={showDetails}
          consolidatedContent
        />
      )}
      {!consolidateContent && contents.sort((a, b) => (sortValue(a) > sortValue(b) ? (sortDirection === 'asc' ? 1 : -1) : (sortDirection === 'asc' ? -1 : 1))).map((content) => {
        const selected = selectedIds.includes(content.id)
        const notSentYet = content?.version
          && sentOrChangeRequestIds.has(content.serviceJobStatusId)
          && !isCurrentVersionDelivered(content)

        return (
          <GridItem
            key={content.id}
            badgeColor={getServiceStatusColor(serviceJobStatuses[content.serviceJobStatusId]?.name)}
            badgeText={serviceJobStatuses[content.serviceJobStatusId]?.description}
            callbacks={{ selectContent: () => selectContent(content) }}
            content={content}
            disableAssetLink={disableAssetLink}
            fileUrl={content.directUrl ? null : `${content.file?.downloadUrl || content.assetUrl}`}
            iconName={serviceGroups[serviceJob.serviceGroupId]?.name}
            link={`/service-jobs/${serviceJob.id}/assets?assetId=${content.id}`}
            multipleSelection={multipleSelection}
            name={content.directUrl ? content.description : content.file?.originalName}
            notSentYet={notSentYet}
            selected={selected}
            showDescription
            showDetails={showDetails}
          />
        )
      })}
    </Grid>
  )
}

GridView.propTypes = {
  callbacks: PropTypes.object,
  consolidateContent: PropTypes.object,
  contents: PropTypes.array,
  disableAssetLink: PropTypes.bool,
  maxSelection: PropTypes.number,
  multipleSelection: PropTypes.bool,
  selectedIds: PropTypes.array,
  showDetails: PropTypes.bool,
}

export default GridView
